import QueryString from 'query-string';
import { toastr } from 'react-redux-toastr';
import { push } from 'react-router-redux';
import { isErrorResponse } from '../../utils/error-handlers';
import {
  DEFAULT_GET_CONFIG,
  DEFAULT_POST_CONFIG,
  DEFAULT_PUT_CONFIG,
  EXCEL_GET_CONFIG,
} from '../../config/rest';
import { ITINERARY_FOOD_INVOICE_ENDPOINT } from '../../config/endpoints';
import { ITINERARY_FOOD_INVOICE_PATH } from '../../config/paths';
import {
  CLEAR_ITINERARY_FOOD_INVOICE,
  CLEAR_ITINERARY_FOOD_INVOICES,
  FLAG_GETTING_ITINERARY_FOOD_INVOICES,
  FLAG_ITINERARY_FOOD_INVOICE_ACTIVITY,
  GET_ITINERARY_FOOD_INVOICE,
  GET_ITINERARY_FOOD_INVOICES,
} from '../types/food/ItineraryFoodInvoice';

const flagGettingItineraryFoodInvoices = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_GETTING_ITINERARY_FOOD_INVOICES,
    payload: flag,
  });

const getItineraryFoodInvoices = async (tableFilters) => async (dispatch) => {
  try {
    dispatch(flagGettingItineraryFoodInvoices(true));

    const query = tableFilters;
    const url = `${ITINERARY_FOOD_INVOICE_ENDPOINT}?${QueryString.stringify(
      query,
    )}`;

    const response = await fetch(url, DEFAULT_GET_CONFIG);
    await isErrorResponse(response, null, dispatch);

    const itineraryFoodInvoices = await response.json();

    dispatch({
      type: GET_ITINERARY_FOOD_INVOICES,
      payload: itineraryFoodInvoices,
    });
  } catch ({ message }) {
    toastr.error('Error', message);
  } finally {
    dispatch(flagGettingItineraryFoodInvoices(false));
  }
};

const clearItineraryFoodInvoices = () => (dispatch) =>
  dispatch({
    type: CLEAR_ITINERARY_FOOD_INVOICES,
  });

const flagItineraryFoodInvoiceActivity = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_ITINERARY_FOOD_INVOICE_ACTIVITY,
    payload: flag,
  });

const getItineraryFoodInvoice =
  async ({ itineraryFoodInvoiceId }) =>
  async (dispatch) => {
    try {
      dispatch(flagItineraryFoodInvoiceActivity(true));

      const url = `${ITINERARY_FOOD_INVOICE_ENDPOINT}/${itineraryFoodInvoiceId}`;
      const response = await fetch(url, DEFAULT_GET_CONFIG);

      await isErrorResponse(response, null, dispatch);

      const itineraryFoodInvoice = await response.json();

      dispatch({
        type: GET_ITINERARY_FOOD_INVOICE,
        payload: itineraryFoodInvoice,
      });
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagItineraryFoodInvoiceActivity(false));
    }
  };

const clearItineraryFoodInvoice = () => (dispatch) =>
  dispatch({
    type: CLEAR_ITINERARY_FOOD_INVOICE,
  });

const postItineraryFoodInvoice =
  async ({
    supplierId,
    documentSeries,
    documentCode,
    amount,
    summaryNumber,
    accountingDeliveredDate,
    issueDate,
    expirationDate,
    periodStartDate,
    periodEndDate,
    itinerarySupplierFoodDetailList,
    description,
  }) =>
  async (dispatch) => {
    try {
      dispatch(flagItineraryFoodInvoiceActivity(true));

      const payload = {
        supplierId,
        documentSeries,
        documentCode,
        amount,
        summaryNumber,
        accountingDeliveredDate,
        issueDate,
        expirationDate,
        periodStartDate,
        periodEndDate,
        itinerarySupplierFoodDetailList,
        description,
      };

      const response = await fetch(ITINERARY_FOOD_INVOICE_ENDPOINT, {
        ...DEFAULT_POST_CONFIG,
        body: JSON.stringify(payload),
      });

      await isErrorResponse(response, null, dispatch);

      const itineraryFoodInvoice = await response.json();

      dispatch(
        push(`${ITINERARY_FOOD_INVOICE_PATH}/${itineraryFoodInvoice.id}`),
      );
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagItineraryFoodInvoiceActivity(false));
    }
  };

const putItineraryFoodInvoice =
  async (
    itineraryFoodInvoiceId,
    {
      supplierId,
      documentSeries,
      documentCode,
      amount,
      summaryNumber,
      accountingDeliveredDate,
      issueDate,
      expirationDate,
      periodStartDate,
      periodEndDate,
      itinerarySupplierFoodDetailList,
      description,
    },
  ) =>
  async (dispatch) => {
    try {
      dispatch(flagItineraryFoodInvoiceActivity(true));

      const payload = {
        id: itineraryFoodInvoiceId,
        supplierId,
        documentSeries,
        documentCode,
        amount,
        summaryNumber,
        accountingDeliveredDate,
        issueDate,
        expirationDate,
        periodStartDate,
        periodEndDate,
        itinerarySupplierFoodDetailList,
        description,
      };

      const url = `${ITINERARY_FOOD_INVOICE_ENDPOINT}/${itineraryFoodInvoiceId}`;

      const response = await fetch(url, {
        ...DEFAULT_PUT_CONFIG,
        body: JSON.stringify(payload),
      });

      await isErrorResponse(response, null, dispatch);

      const itineraryFoodInvoice = await response.json();

      dispatch(
        push(`${ITINERARY_FOOD_INVOICE_PATH}/${itineraryFoodInvoice.id}`),
      );
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagItineraryFoodInvoiceActivity(false));
    }
  };

const deleteItineraryFoodInvoice =
  async (itineraryFoodInvoiceId) => async (dispatch) => {
    try {
      dispatch(flagItineraryFoodInvoiceActivity(true));

      const url = `${ITINERARY_FOOD_INVOICE_ENDPOINT}/${itineraryFoodInvoiceId}`;

      const response = await fetch(url, {
        ...DEFAULT_GET_CONFIG,
        method: 'DELETE',
      });

      await isErrorResponse(response, null, dispatch);

      dispatch(push(ITINERARY_FOOD_INVOICE_PATH));
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagItineraryFoodInvoiceActivity(false));
    }
  };

const getItineraryFoodInvoiceSummaryExcel =
  async ({ summaryNumber }) =>
  async (dispatch) => {
    try {
      dispatch(flagGettingItineraryFoodInvoices(true));

      const query = {
        summaryNumber,
      };

      const url = `${ITINERARY_FOOD_INVOICE_ENDPOINT}/summary/?${QueryString.stringify(
        query,
      )}`;

      const response = await fetch(url, EXCEL_GET_CONFIG);

      await isErrorResponse(response, null, dispatch);

      const blob = await response.blob();

      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = downloadUrl;

      const contentDisposition = response.headers.get('Content-Disposition');

      let fileName = `resumen-factura-alimentacion-${summaryNumber}.xlsx`;

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+)"?/);
        if (match) {
          [, fileName] = match;
        }
      }

      link.setAttribute('download', fileName);

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);

      window.URL.revokeObjectURL(downloadUrl);
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagGettingItineraryFoodInvoices(false));
    }
  };

export {
  flagGettingItineraryFoodInvoices,
  getItineraryFoodInvoices,
  clearItineraryFoodInvoices,
  flagItineraryFoodInvoiceActivity,
  getItineraryFoodInvoice,
  clearItineraryFoodInvoice,
  postItineraryFoodInvoice,
  putItineraryFoodInvoice,
  deleteItineraryFoodInvoice,
  getItineraryFoodInvoiceSummaryExcel,
};
